import { useEffect, useRef, useState } from "react";
import { useAsk, type Answer } from "../api/hooks";
import { Button, Chip, ErrorBox } from "./ui";
import { title } from "../lib/format";

type Turn = { question: string; answer?: Answer; error?: unknown };

const starters = ["How is after-hours looking this week?", "Where is meeting load highest?", "Are 1:1s happening?", "What is the cycle time trend?"];

/** The "Ask Humindly AI" drawer: one question at a time, answered from the aggregate evidence pack. Nothing is stored. */
export function AskDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const ask = useAsk();
  const [q, setQ] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => { if (open) box.current?.focus(); }, [open]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const submit = (question: string) => {
    const text = question.trim();
    if (!text || ask.isPending) return;
    setQ("");
    setTurns((t) => [...t, { question: text }]);
    ask.mutate({ question: text }, {
      onSuccess: (a) => setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, answer: a } : x))),
      onError: (e) => setTurns((t) => t.map((x, i) => (i === t.length - 1 ? { ...x, error: e } : x))),
    });
  };
  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <aside className="drawer" onClick={(e) => e.stopPropagation()} aria-label="Ask Humindly AI">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-extrabold text-[16px] tracking-tight">✦ Ask Humindly AI</h3>
            <p className="text-[12.5px] text-muted mt-1">Answers come from this week's aggregate signals and briefing — never from message or meeting content. Questions are not stored.</p>
          </div>
          <button className="link" onClick={onClose}>Close</button>
        </div>
        <div className="flex-1 overflow-y-auto mt-4 flex flex-col gap-3 pr-1">
          {turns.length === 0 && (
            <div className="flex flex-col gap-2">
              {starters.map((s) => <button key={s} className="btn btn-light text-left !justify-start !py-2 !text-[12.5px]" onClick={() => submit(s)}>{s}</button>)}
            </div>
          )}
          {turns.map((t, i) => (
            <div key={i}>
              <div className="rounded-xl bg-nav-active text-link px-3 py-2 text-[13px] font-semibold">{t.question}</div>
              <div className="mt-2 text-[13px] leading-6">
                {t.error != null ? <ErrorBox error={t.error} /> : t.answer ? (
                  <>
                    <p>{t.answer.answer}</p>
                    {t.answer.sources.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2">{t.answer.sources.map((s) => <Chip key={s}>{title(s).replace(" Pct", " %")}</Chip>)}</div>}
                    {t.answer.caveats.map((c, j) => <p key={j} className="text-[11.5px] text-muted mt-1.5">{c}</p>)}
                    <p className="text-[11px] text-muted mt-1">{title(t.answer.scope_type)} · {t.answer.scope_key} · week of {t.answer.period_start} · {t.answer.model === "template" ? "rules-based" : t.answer.model}</p>
                  </>
                ) : <span className="text-muted">Thinking…</span>}
              </div>
            </div>
          ))}
        </div>
        <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); submit(q); }}>
          <input ref={box} className="input flex-1" placeholder="Ask about your workforce…" maxLength={500} value={q} onChange={(e) => setQ(e.target.value)} />
          <Button type="submit" disabled={ask.isPending || !q.trim()}>Ask</Button>
        </form>
      </aside>
    </div>
  );
}
